import { useState } from 'react';

import {
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Check,
  Col,
  DNTCaptcha,
  DNTCaptchaRealm,
  type DNTCaptchaRef,
  HookForm,
  Row
} from '@dec/shared';
import { useGetHomeTemplate } from 'app/modules/Parameters/Home/hook';
import { ButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useTaxpayerRequestStore } from '../../stores/useTaxpayerRequestStore';
import ProgressBar from '../components/progress-bar';
import { useTaxpayerRequestActions } from '../hooks';
import { label } from '../translation';
import { type Steps } from '../types';

import { type ProgressStepProps } from './types';

interface FormProps {
  captchaError?: string;
  onCaptchaLoadStateChange: (isReady: boolean) => void;
  onSubmit: (data: any) => Promise<void>;
}
const Form = ({ captchaError, onCaptchaLoadStateChange, onSubmit }: FormProps) => {
  const { t, i18n } = useTranslation();
  const { data, error, loading, execute } = useGetHomeTemplate(i18n.language, 'terms-template');
  let dntCaptcha: DNTCaptchaRef;

  const rules = yup.object().shape({
    confirmTerms: yup.bool().oneOf([true], label(t, 'acceptance'))
  });

  const submit = async (data: any) => {
    await onSubmit({
      ...data,
      ...(dntCaptcha ? (dntCaptcha.getResponse() as any) : {})
    });
    dntCaptcha && dntCaptcha.refresh();
  };

  if (loading) {
    return <></>;
  }
  return (
    <HookForm nameForm={'formAcceptance'} validations={rules} onSubmit={submit}>
      <Row>
        <Col>
          <div className="content" dangerouslySetInnerHTML={{ __html: data?.template || '' }}></div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Check name="confirmTerms" label={label(t, 'confirmTerms')} />
        </Col>

        <Col className="align-items-sm-start align-items-lg-end align-items-md-end align-items-xl-end">
          <DNTCaptcha
            realm={DNTCaptchaRealm.Application}
            dntCaptchaRef={(resp: DNTCaptchaRef) => (dntCaptcha = resp)}
            onLoadStateChange={onCaptchaLoadStateChange}
            placeholder={t('common.label.captcha-placeholder')}
          />
          {captchaError && <div className="invalid-feedback d-block">{captchaError}</div>}
        </Col>
      </Row>
    </HookForm>
  );
};

const Acceptance = (props: ProgressStepProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [captchaError, setCaptchaError] = useState<string>();
  const [captchaReady, setCaptchaReady] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const currentData = useTaxpayerRequestStore((s) => s.form);
  const { saveRequest, updateRequest } = useTaxpayerRequestActions();

  const check = () => {
    let ok = true;
    if (!currentData.operations || currentData.operations?.length === 0) ok = false;
    if (!currentData.contacts || currentData.contacts.length === 0) ok = false;
    //if (!currentData.documents || Object.keys(currentData.documents).length === 0) ok = false;
    if (!currentData.general || Object.keys(currentData.general).length === 0) ok = false;
    if (!currentData.tax || Object.keys(currentData.tax).length === 0) ok = false;
    //if (!currentData.taxes || currentData.taxes?.length === 0) ok = false;
    // if (!currentData.reports || currentData.reports?.length === 0) ok = false;
    //return ok;
    return true;
  };

  const onSubmit = async (data: {
    DNTCaptchaInputText: string;
    DNTCaptchaText: string;
    DNTCaptchaToken: string;
  }) => {
    if (!captchaReady || !data.DNTCaptchaInputText?.trim()) {
      setCaptchaError(t('common.validations.fieldRequired'));
      return;
    }

    if (check()) {
      setSubmitting(true);
      setCaptchaError(undefined);
      try {
        if (!id) {
          await saveRequest({
            data: currentData as Steps,
            captcha: {
              DNTCaptchaInputText: data.DNTCaptchaInputText,
              DNTCaptchaText: data.DNTCaptchaText,
              DNTCaptchaToken: data.DNTCaptchaToken
            },
            redirectMeta: {
              path: `/public/taxpayer-request/success`
            }
          });
        } else {
          await updateRequest({
            id,
            data: currentData as Steps,
            redirectMeta: {
              path: -1
            }
          });
        }
      } catch (error: any) {
        const captchaValidationError = error?.response?.data?.errors?.find(
          (validationError: any) =>
            validationError?.codeError === 'captcha' || validationError?.CodeError === 'captcha'
        );

        if (captchaValidationError) {
          setCaptchaError(
            captchaValidationError.errorMessage ?? captchaValidationError.ErrorMessage
          );
        }
      } finally {
        setSubmitting(false);
      }
    } else {
      setDirty(true);
    }
  };

  return (
    <>
      {' '}
      <Form
        captchaError={captchaError}
        onCaptchaLoadStateChange={setCaptchaReady}
        onSubmit={onSubmit}
      />
      {dirty && !check() && (
        <div className="invalid-feedback d-block">
          {t('taxpayer-request.validations.formComplete')}
        </div>
      )}
      <ProgressBar percentage={props.percentage} />
      <BtnToolbar>
        <ButtonGroup>
          <Button
            form="formTextData"
            action={ButtonAction.previous}
            disabled={submitting}
            onClick={() => props.previousStep()}
          />
        </ButtonGroup>
        <BtnGroup>
          <Button
            variant="primary"
            form="formAcceptance"
            action={ButtonAction.upload}
            type={'submit'}
            text={t('common.label.send')}
            disabled={submitting || !captchaReady}
          />
        </BtnGroup>
      </BtnToolbar>
    </>
  );
};

export default Acceptance;
